"use client";

import React, { useRef } from "react";
import { Building2, ShieldCheck, Clock, Users, MapPin, ArrowRight, CircleCheck as CheckCircle2, Layers, Factory, Wrench } from 'lucide-react';
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const fmNeeds = [{
  title: "Responsive access & maintenance",
  description: "Fast turnarounds for reactive roof repairs, guttering, window replacements, and ongoing planned preventive maintenance (PPM).",
  icon: Clock
}, {
  title: "Minimal disruption to occupants",
  description: "Carefully phased erections, pedestrian gantries, debris netting, and clean access routes designed around tenants, staff, and public flow.",
  icon: Users
}, {
  title: "Dependable site coordination",
  description: "Clear communication with estate managers, concierge teams, and multi-trade contractors to keep schedules on track without surprises.",
  icon: Wrench
}, {
  title: "Audited safety accreditations",
  description: "Fully compliant TG20:21 designs, regular statutory 7-day scaffold inspections, handover handover certificates, and complete RAMS.",
  icon: ShieldCheck
}];
const relatedServices = [{
  title: "Commercial scaffolding",
  description: "High-spec access solutions for office buildings, retail parks, schools, and healthcare facilities.",
  to: "/CommercialScaffoldingPage",
  icon: Building2,
  badge: "Core Service"
}, {
  title: "Refurbishment scaffolding",
  description: "Tailored external and internal access for comprehensive structural repairs, re-cladding, and cosmetic upgrades.",
  to: "/RefurbishmentScaffoldingPage",
  icon: Layers,
  badge: "Specialist"
}, {
  title: "Industrial scaffolding",
  description: "Heavy-duty loading bays, plant room access, and suspended platforms for logistics hubs and manufacturing plants.",
  to: "/IndustrialScaffoldingPage",
  icon: Factory,
  badge: "Heavy Duty"
}];
const coverageAreas = ["Berkshire", "Surrey", "Hampshire", "Buckinghamshire", "London", "Wider South East"];
const accreditations = ["NASC Compliant Standards", "CHAS Accredited", "CISRS Qualified Scaffolders"];
export default function Facilities() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".gsap-fade-up", {
        y: 28
      });
      gsap.to(".gsap-fade-up", {
        opacity: 1,
        y: 0,
        duration: 0.65,
        stagger: 0.08,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".gsap-fade-up", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section id="propertyfacilitiesmanagementopening" data-nav="light" ref={container} className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Header Eyebrow & Badges */}
        <div className="flex flex-wrap items-center gap-2 mb-4 gsap-fade-up" style={{
        opacity: 0
      }}>
          <Badge variant="outline" className="border-border text-foreground px-3 py-1 font-medium text-xs">
            Sector Solutions
          </Badge>
          <span className="text-muted-foreground text-xs hidden sm:inline">•</span>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            <span>NASC • CHAS</span>
          </div>
        </div>

        {/* Hero Grid: H1 & Answer-First Framing */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-16">
          <div className="lg:col-span-7">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground leading-tight mb-6 gsap-fade-up" style={{
            opacity: 0
          }}>
              Scaffolding for property & facilities management
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8 gsap-fade-up" style={{
            opacity: 0
          }}>
              Property managers, building surveyors, and FM directors require responsive access for scheduled maintenance and urgent repairs — without compromising building security, tenant safety, or day-to-day operations.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 gsap-fade-up" style={{
            opacity: 0
          }}>
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactRequestQuotePage">
                  Request site survey & quote
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent">
                <Link to="/ProjectsCaseStudiesPage">
                  View recent projects
                </Link>
              </Button>
            </div>
          </div>

          {/* Quick Facts / Support Summary Card */}
          <div className="lg:col-span-5 gsap-fade-up" style={{
          opacity: 0
        }}>
            <div className="bg-card border border-border rounded-xl p-6 sm:p-7 shadow-sm">
              <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                How BMF supports your estate
              </h2>
              <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                Backed by 20+ years of operational leadership under Pete Wood, we provide a commercially serious, safety-led scaffolding partnership built for occupied commercial portfolios and residential blocks.
              </p>
              
              <ul className="space-y-3 mb-6">
                {accreditations.map((item, idx) => <li key={idx} data-index={idx} className="flex items-center gap-2.5 text-sm text-foreground">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>)}
              </ul>

              <Separator className="my-5" />

              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                    Coverage Area
                  </span>
                  <p className="text-xs text-foreground leading-normal">
                    Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the South East.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: What Property & FM Clients Need */}
        <div className="mb-20">
          <div className="max-w-2xl mb-10 gsap-fade-up" style={{
          opacity: 0
        }}>
            <h2 className="text-2xl sm:text-3xl font-default font-bold text-foreground mb-3">
              Built around the realities of occupied estates
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base">
              Scaffolding in live environments demands higher standards of cleanliness, protection, and strict schedule adherence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {fmNeeds.map((need, idx) => {
            const IconComp = need.icon;
            return <Card key={idx} data-index={idx} className="gsap-fade-up bg-card border-border shadow-xs hover:shadow-md transition-shadow" style={{
              opacity: 0
            }}>
                  <CardHeader className="pb-3">
                    <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center mb-2">
                      <IconComp className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-base font-semibold text-foreground leading-snug">
                      {need.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {need.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Section 3: Relevant Scaffolding Services */}
        <div className="mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4 gsap-fade-up" style={{
          opacity: 0
        }}>
            <div>
              <h2 className="text-2xl sm:text-3xl font-default font-bold text-foreground mb-2">
                Specialist services for facilities teams
              </h2>
              <p className="text-muted-foreground text-sm sm:text-base">
                Explore our purpose-engineered structures suited for complex maintenance and major works.
              </p>
            </div>
            <Button asChild variant="outline" className="border-border self-start md:self-auto">
              <Link to="/CommercialScaffoldingPage">
                All commercial solutions
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedServices.map((service, idx) => {
            const IconComp = service.icon;
            return <div key={idx} data-index={idx} className="gsap-fade-up flex flex-col justify-between bg-card border border-border rounded-xl p-6 shadow-xs hover:border-primary/50 transition-colors" style={{
              opacity: 0
            }}>
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-11 w-11 rounded-lg bg-accent flex items-center justify-center">
                        <IconComp className="h-6 w-6 text-primary" />
                      </div>
                      <Badge variant="secondary" className="text-xs bg-muted text-foreground font-normal">
                        {service.badge}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                      {service.description}
                    </p>
                  </div>

                  <div>
                    <Button asChild variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80 font-medium text-sm flex items-center gap-1.5">
                      <Link to={service.to}>
                        <span>Service specifications</span>
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Section 4: Geographic Coverage Strip */}
        <div className="bg-accent/60 border border-border rounded-xl p-6 sm:p-8 gsap-fade-up" style={{
        opacity: 0
      }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-4">
              <h3 className="text-lg font-bold text-foreground flex items-center gap-2 mb-1">
                <MapPin className="h-5 w-5 text-primary shrink-0" />
                South East & Home Counties
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Rapid mobilization and scheduled site teams across all primary regions.
              </p>
            </div>

            <div className="lg:col-span-8 flex flex-wrap items-center gap-2 sm:gap-3">
              {coverageAreas.map((area, idx) => <span key={idx} data-index={idx} className="inline-flex items-center px-3.5 py-1.5 rounded-md bg-card border border-border text-xs sm:text-sm font-medium text-foreground shadow-2xs">
                  {area}
                </span>)}
              <Button asChild variant="link" className="text-primary text-xs sm:text-sm font-semibold pl-2">
                <Link to="/AreasWeCoverPage">
                  View full service radius &rarr;
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}